import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ResumePreview } from "@/components/ResumePreview";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  ArrowLeft, Plus, Trash2, Wand2, Save, Printer, Loader2, Check,
} from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = ["modern", "classic", "minimal"];

const emptyData = {
  fullName: "", jobTitle: "", email: "", phone: "", location: "", website: "",
  summary: "", skills: "", experience: [], education: [],
};

const Builder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [title, setTitle] = useState("Untitled Resume");
  const [template, setTemplate] = useState("modern");
  const [data, setData] = useState(emptyData);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedTick, setSavedTick] = useState(false);
  const [aiLoading, setAiLoading] = useState("");
  const saveTimer = useRef(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/resumes/${id}`);
        setTitle(res.data.title);
        setTemplate(res.data.template || "modern");
        setData({ ...emptyData, ...(res.data.data || {}) });
      } catch (e) {
        toast.error("Resume not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    };
    if (user) load();
  }, [id, user, navigate]);

  const doSave = useCallback(async (silent) => {
    setSaving(true);
    try {
      await api.put(`/resumes/${id}`, { title, template, data });
      if (!silent) toast.success("Resume saved");
      setSavedTick(true);
      setTimeout(() => setSavedTick(false), 1500);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [id, title, template, data]);

  // autosave
  useEffect(() => {
    if (loading) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(true), 1200);
    return () => clearTimeout(saveTimer.current);
  }, [title, template, data, loading, doSave]);

  const update = (field, value) => setData((p) => ({ ...p, [field]: value }));

  const updateList = (list, idx, field, value) => {
    setData((p) => {
      const arr = [...(p[list] || [])];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...p, [list]: arr };
    });
  };
  const addItem = (list, template) => setData((p) => ({ ...p, [list]: [...(p[list] || []), template] }));
  const removeItem = (list, idx) => setData((p) => ({ ...p, [list]: p[list].filter((_, i) => i !== idx) }));

  const generateAI = async (type) => {
    setAiLoading(type);
    try {
      const context = {
        name: data.fullName, jobTitle: data.jobTitle,
        experience: data.experience, skills: data.skills, summary: data.summary,
      };
      const res = await api.post("/ai/generate", { type, context });
      const result = res.data.result?.trim();
      if (type === "summary") update("summary", result);
      if (type === "skills") update("skills", result);
      toast.success("AI content added");
    } catch (e) {
      toast.error("AI generation failed");
    } finally {
      setAiLoading("");
    }
  };

  const generateBullets = async (idx) => {
    setAiLoading(`bullets-${idx}`);
    try {
      const exp = data.experience[idx];
      const res = await api.post("/ai/generate", {
        type: "bullets",
        context: { role: exp.role, company: exp.company, jobTitle: data.jobTitle },
      });
      updateList("experience", idx, "bullets", res.data.result?.trim());
      toast.success("Bullet points generated");
    } catch (e) {
      toast.error("AI generation failed");
    } finally {
      setAiLoading("");
    }
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] lg:h-[calc(100vh-4rem)] flex flex-col font-body" data-testid="builder">
      {/* Top bar */}
      <div className="border-b border-border bg-card px-5 py-3 flex items-center justify-between gap-3 flex-wrap print:hidden">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="max-w-xs font-heading font-semibold border-transparent hover:border-border focus:border-border"
            data-testid="resume-title-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border border-border overflow-hidden">
            {TEMPLATES.map((t) => (
              <button
                key={t}
                onClick={() => setTemplate(t)}
                data-testid={`template-${t}`}
                className={`text-xs px-3 py-1.5 capitalize transition-colors ${template === t ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
              >
                {t}
              </button>
            ))}
          </div>
          <Button onClick={() => doSave(false)} disabled={saving} variant="outline" className="rounded-full" data-testid="save-btn">
            {savedTick ? <Check className="h-4 w-4 mr-1.5 text-green-600" /> : <Save className="h-4 w-4 mr-1.5" />}
            {saving ? "Saving…" : "Save"}
          </Button>
          <Button onClick={() => window.print()} className="rounded-full" data-testid="print-btn">
            <Printer className="h-4 w-4 mr-1.5" /> Print / PDF
          </Button>
        </div>
      </div>

      {/* Split pane */}
      <div className="flex-1 grid lg:grid-cols-2 lg:overflow-hidden">
        {/* Form */}
        <div className="lg:overflow-y-auto p-6 space-y-6 print:hidden">
          <Section title="Personal Details">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Full name"><Input value={data.fullName} onChange={(e) => update("fullName", e.target.value)} data-testid="input-fullName" /></Field>
              <Field label="Job title"><Input value={data.jobTitle} onChange={(e) => update("jobTitle", e.target.value)} data-testid="input-jobTitle" /></Field>
              <Field label="Email"><Input value={data.email} onChange={(e) => update("email", e.target.value)} data-testid="input-email" /></Field>
              <Field label="Phone"><Input value={data.phone} onChange={(e) => update("phone", e.target.value)} data-testid="input-phone" /></Field>
              <Field label="Location"><Input value={data.location} onChange={(e) => update("location", e.target.value)} data-testid="input-location" /></Field>
              <Field label="Website"><Input value={data.website} onChange={(e) => update("website", e.target.value)} data-testid="input-website" /></Field>
            </div>
          </Section>

          <Section title="Professional Summary" action={
            <AiButton loading={aiLoading === "summary"} onClick={() => generateAI("summary")} testid="ai-summary-btn" />
          }>
            <Textarea rows={4} value={data.summary} onChange={(e) => update("summary", e.target.value)} placeholder="A short professional summary…" data-testid="input-summary" />
          </Section>

          <Section title="Experience" action={
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => addItem("experience", { company: "", role: "", start: "", end: "", bullets: "" })} data-testid="add-experience-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          }>
            {(data.experience || []).map((exp, i) => (
              <Card key={i} className="p-4 space-y-3" data-testid={`experience-${i}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-muted-foreground">Position {i + 1}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeItem("experience", i)} data-testid={`remove-experience-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input placeholder="Role" value={exp.role} onChange={(e) => updateList("experience", i, "role", e.target.value)} data-testid={`exp-role-${i}`} />
                  <Input placeholder="Company" value={exp.company} onChange={(e) => updateList("experience", i, "company", e.target.value)} data-testid={`exp-company-${i}`} />
                  <Input placeholder="Start (e.g. 2021)" value={exp.start} onChange={(e) => updateList("experience", i, "start", e.target.value)} data-testid={`exp-start-${i}`} />
                  <Input placeholder="End (e.g. Present)" value={exp.end} onChange={(e) => updateList("experience", i, "end", e.target.value)} data-testid={`exp-end-${i}`} />
                </div>
                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <Label className="text-xs">Bullet points (one per line)</Label>
                    <AiButton loading={aiLoading === `bullets-${i}`} onClick={() => generateBullets(i)} testid={`ai-bullets-${i}`} label="AI write" />
                  </div>
                  <Textarea rows={4} value={exp.bullets} onChange={(e) => updateList("experience", i, "bullets", e.target.value)} placeholder="- Achieved X by doing Y…" data-testid={`exp-bullets-${i}`} />
                </div>
              </Card>
            ))}
          </Section>

          <Section title="Education" action={
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => addItem("education", { school: "", degree: "", start: "", end: "" })} data-testid="add-education-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add
            </Button>
          }>
            {(data.education || []).map((ed, i) => (
              <Card key={i} className="p-4 space-y-3" data-testid={`education-${i}`}>
                <div className="flex justify-between items-center">
                  <span className="text-xs font-semibold text-muted-foreground">Education {i + 1}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeItem("education", i)} data-testid={`remove-education-${i}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input placeholder="Degree" value={ed.degree} onChange={(e) => updateList("education", i, "degree", e.target.value)} data-testid={`edu-degree-${i}`} />
                  <Input placeholder="School" value={ed.school} onChange={(e) => updateList("education", i, "school", e.target.value)} data-testid={`edu-school-${i}`} />
                  <Input placeholder="Start" value={ed.start} onChange={(e) => updateList("education", i, "start", e.target.value)} data-testid={`edu-start-${i}`} />
                  <Input placeholder="End" value={ed.end} onChange={(e) => updateList("education", i, "end", e.target.value)} data-testid={`edu-end-${i}`} />
                </div>
              </Card>
            ))}
          </Section>

          <Section title="Skills" action={
            <AiButton loading={aiLoading === "skills"} onClick={() => generateAI("skills")} testid="ai-skills-btn" />
          }>
            <Input value={data.skills} onChange={(e) => update("skills", e.target.value)} placeholder="Comma separated, e.g. React, Leadership, SQL" data-testid="input-skills" />
          </Section>
        </div>

        {/* Preview */}
        <div className="lg:overflow-y-auto workspace-bg p-6 lg:p-10 flex justify-center print:overflow-visible print:p-0 print:bg-white">
          <div className="w-full max-w-[520px] print:max-w-none" id="resume-print-area">
            <ResumePreview data={data} template={template} />
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, action, children }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <h3 className="font-heading font-semibold text-lg">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    {children}
  </div>
);

const AiButton = ({ loading, onClick, testid, label = "AI generate" }) => (
  <Button size="sm" variant="outline" onClick={onClick} disabled={loading} className="rounded-full border-primary/40 text-primary hover:bg-primary/10" data-testid={testid}>
    {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Wand2 className="h-3.5 w-3.5 mr-1" />}
    {label}
  </Button>
);

export default Builder;
